import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { MessageAtom, useMediaQuery } from 'design-system/my-lib';
import { STUDIO_BUILDERS, StudioBuilder } from './builders';
import { StudioModeProvider } from './StudioMode';
import StudioModePill from './StudioModePill';
import './StudioShell.scss';

/** Where a warm entry came from — carried as `location.state.from`. `id` is the sphere id for
 * `sphere`, the destination path for `link`, and unused for `finding` (findings only ever open
 * from the desk, so their origin is always `/studio` itself). */
export type StudioFromKind = 'sphere' | 'finding' | 'link';

export interface StudioFromContext {
  kind: StudioFromKind;
  id?: string;
}

/** Pure so it can be unit-tested without mounting the router. `/studio` is the fallback for both
 * `finding` and a `link`/`sphere` entry with no usable id — "the from route no longer exists"
 * edge case in the spec resolves to the same place a missing `from` does. */
export function resolveFromRoute(from: StudioFromContext): string {
  switch (from.kind) {
    case 'sphere':
      return from.id ? `/sphere/${from.id}` : '/studio';
    case 'link':
      return from.id ?? '/studio';
    case 'finding':
    default:
      return '/studio';
  }
}

const NAV_ITEM_CLASS = 'studio-shell__nav-item';
const NAV_ITEM_ACTIVE_CLASS = `${NAV_ITEM_CLASS} ${NAV_ITEM_CLASS}--active`;

/**
 * Collapsed/expanded is a per-viewer preference, so it persists rather than resetting on every
 * navigation. localStorage (not sessionStorage) because the choice should survive a reload; reads
 * and writes are wrapped because a browser with site data blocked throws on access rather than
 * returning null.
 */
const SIDEBAR_COLLAPSED_KEY = 'center.studio.sidebarCollapsed';

function readCollapsedPreference(): boolean {
  try {
    return globalThis.localStorage?.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsedPreference(collapsed: boolean): void {
  try {
    globalThis.localStorage?.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    /* preference is a convenience — never let a storage failure break the shell */
  }
}

/**
 * Match on a full path segment, not a raw prefix — `startsWith('/studio/space')` would also
 * claim a future `/studio/spaces` route.
 */
function matchesSegment(pathname: string, segment: string): boolean {
  const base = `/studio/${segment}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * The trail beyond the builder — e.g. `/studio/space/abc` is inside the Space builder but on a
 * specific document, and `/studio/composition/Foo` is a built-in preview. Without this the
 * breadcrumb reads "Studio > Space" from the gallery AND from three levels into an editor, which
 * is what made it look like it was not tracking anything (owner report, 2026-08-28).
 */
export function findLeafCrumbKey(pathname: string, builder: StudioBuilder | undefined): string | null {
  if (!builder) return null;
  const segments = pathname.split('/').filter(Boolean); // ['studio', '<segment>', ...rest]
  return segments.length > 2 ? 'studio.shell.breadcrumbDocument' : null;
}

export function findCurrentBuilder(pathname: string): StudioBuilder | undefined {
  return STUDIO_BUILDERS.find(
    (builder) =>
      matchesSegment(pathname, builder.segment) ||
      (builder.aliasSegments ?? []).some((alias) => matchesSegment(pathname, alias))
  );
}

/**
 * The breadcrumb/title text for the matched builder at this path — the builder's own `labelKey`
 * when the primary segment matched, or the alias's override when an aliased route (e.g. the
 * Sphere Type Editor under `/studio/types`) matched instead. See `aliasLabelKeys` on `StudioBuilder`.
 */
export function currentBuilderLabelKey(
  pathname: string,
  builder: StudioBuilder | undefined
): string | undefined {
  if (!builder) return undefined;
  if (matchesSegment(pathname, builder.segment)) return builder.labelKey;
  const alias = (builder.aliasSegments ?? []).find((segment) => matchesSegment(pathname, segment));
  return (alias && builder.aliasLabelKeys?.[alias]) || builder.labelKey;
}

/**
 * The Studio shell (EAINT-858) — builder nav + breadcrumb + `<Outlet/>`, replacing the bare
 * `<Outlet/>` Phase 1 left at `/studio` in app.tsx. Mirrors the `OrgSettingsShell` /
 * `MetasphereAdminShell` pattern, but the mobile story differs deliberately: Studio inherits the
 * composer's hard desktop-only guard (a full block below 1023px, matching `ComposeGalleryPage`)
 * rather than a collapsible-sidebar breakpoint.
 *
 * The sidebar collapse here is therefore a DESKTOP affordance, not the siblings' mobile off-canvas
 * drawer: it narrows the rail to icons so the canvas gets the width back, and the preference
 * persists per viewer. Labels are visually hidden rather than unmounted so the links keep their
 * accessible names.
 */
const StudioShell: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const logAnalyticsEvent = useAnalytics();
  const belowDesktop = useMediaQuery('(max-width: 1023px)');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedPreference);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((collapsed) => {
      writeCollapsedPreference(!collapsed);
      return !collapsed;
    });
  }, []);

  const navigationState = location.state as { entry?: string; from?: StudioFromContext } | null;
  const from = navigationState?.from;

  const handleBack = useCallback(() => {
    if (!from) return;
    navigate(resolveFromRoute(from));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from]);

  // Fire studio_opened once when the shell mounts. entry: 'nav' when the global-nav button set
  // navigation state, 'redirect' when a legacy /compose* redirect landed here, 'url' otherwise
  // (bookmark / direct URL / typed address). fromKind is only present on a warm entry.
  useEffect(() => {
    const entry = navigationState?.entry ?? 'url';
    logAnalyticsEvent('studio_opened', undefined, { entry, fromKind: from?.kind });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire studio_builder_opened whenever the matched route lands on a registered builder — the
  // click-through signal that shows which scaffolded builder people actually reach for.
  useEffect(() => {
    const current = findCurrentBuilder(location.pathname);
    if (!current) return;
    logAnalyticsEvent('studio_builder_opened', undefined, {
      builderId: current.id,
      status: current.status,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (belowDesktop) {
    return (
      <div className="studio-shell studio-shell--desktop-only">
        <MessageAtom severity="info" text={t('composer.page.desktopOnlyTitle')} />
        <p className="studio-shell__desktop-only-description">
          {t('composer.page.desktopOnlyDescription')}
        </p>
      </div>
    );
  }

  const currentBuilder = findCurrentBuilder(location.pathname);
  const leafCrumbKey = findLeafCrumbKey(location.pathname, currentBuilder);
  const currentBuilderCrumbLabelKey = currentBuilderLabelKey(location.pathname, currentBuilder);

  return (
    <StudioModeProvider>
      <div className={`studio-shell${sidebarCollapsed ? ' studio-shell--sidebar-collapsed' : ''}`}>
      <aside className="studio-shell__sidebar" aria-label={t('studio.shell.sidebarLabel')}>
        <div className="studio-shell__sidebar-header">
          {/* Title is hidden from layout when collapsed but kept for screen readers, so the
              landmark keeps its name at both widths. */}
          <h2
            className={`studio-shell__title${
              sidebarCollapsed ? ' studio-shell__title--sr-only' : ''
            }`}
          >
            {t('studio.shell.title')}
          </h2>
          <button
            type="button"
            className="studio-shell__sidebar-toggle"
            onClick={toggleSidebar}
            aria-expanded={!sidebarCollapsed}
            aria-label={t(
              sidebarCollapsed ? 'studio.shell.expandSidebar' : 'studio.shell.collapseSidebar'
            )}
            title={t(
              sidebarCollapsed ? 'studio.shell.expandSidebar' : 'studio.shell.collapseSidebar'
            )}
            data-testid="studio-sidebar-toggle"
          >
            <i
              className={`pi ${sidebarCollapsed ? 'pi-angle-double-right' : 'pi-angle-double-left'}`}
              aria-hidden="true"
            />
          </button>
        </div>
        <nav className="studio-shell__nav" aria-label={t('studio.shell.navLabel')}>
          {STUDIO_BUILDERS.map((builder) => (
            <NavLink
              key={builder.id}
              to={`/studio/${builder.segment}`}
              className={
                currentBuilder?.id === builder.id ? NAV_ITEM_ACTIVE_CLASS : NAV_ITEM_CLASS
              }
              data-testid={`studio-nav-${builder.id}`}
              // Native tooltip only when collapsed — the label is the only thing that identifies
              // the builder once it is visually hidden.
              title={sidebarCollapsed ? t(builder.labelKey) : undefined}
            >
              <i className={`${builder.icon} studio-shell__nav-icon`} aria-hidden="true" />
              {/* The label is visually hidden, never unmounted: an icon-only link with no
                  accessible name is unusable to a screen reader, and removing it would also
                  break the existing nav tests that query by label text. */}
              <span
                className={`studio-shell__nav-label${
                  sidebarCollapsed ? ' studio-shell__nav-label--sr-only' : ''
                }`}
              >
                {t(builder.labelKey)}
              </span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="studio-shell__body">
        {/* Every crumb except the last is a real link — a breadcrumb that cannot be clicked is
            decoration. `aria-current="page"` marks the leaf, which is the one that must NOT be a
            link. */}
        <nav className="studio-shell__breadcrumb" aria-label={t('studio.shell.breadcrumbLabel')}>
          {from && (
            <button
              type="button"
              className="studio-shell__back"
              onClick={handleBack}
              data-testid="studio-back"
            >
              <i className="pi pi-arrow-left" aria-hidden="true" />
              {t('studio.shell.back')}
            </button>
          )}
          {currentBuilder ? (
            <Link className="studio-shell__breadcrumb-link" to="/studio" data-testid="studio-crumb-root">
              {t('studio.shell.title')}
            </Link>
          ) : (
            <span className="studio-shell__breadcrumb-current" aria-current="page" data-testid="studio-crumb-root">
              {t('studio.shell.title')}
            </span>
          )}
          {currentBuilder && (
            <>
              <i className="pi pi-angle-right studio-shell__breadcrumb-separator" aria-hidden="true" />
              {leafCrumbKey ? (
                <Link
                  className="studio-shell__breadcrumb-link"
                  to={`/studio/${currentBuilder.segment}`}
                  data-testid="studio-crumb-builder"
                >
                  {t(currentBuilderCrumbLabelKey ?? currentBuilder.labelKey)}
                </Link>
              ) : (
                <span
                  className="studio-shell__breadcrumb-current"
                  aria-current="page"
                  data-testid="studio-crumb-builder"
                >
                  {t(currentBuilderCrumbLabelKey ?? currentBuilder.labelKey)}
                </span>
              )}
            </>
          )}
          {leafCrumbKey && (
            <>
              <i className="pi pi-angle-right studio-shell__breadcrumb-separator" aria-hidden="true" />
              <span
                className="studio-shell__breadcrumb-current"
                aria-current="page"
                data-testid="studio-crumb-leaf"
              >
                {t(leafCrumbKey)}
              </span>
            </>
          )}
          <div className="studio-shell__breadcrumb-trailing">
            <StudioModePill />
          </div>
        </nav>
        <main className="studio-shell__main">
          <Outlet />
        </main>
      </div>
    </div>
    </StudioModeProvider>
  );
};

export default StudioShell;
